import type { ReactNode } from 'react';
import React, { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme, _params) => ({
  root: {
    width: '100%',
    margin: 'auto',
    marginTop: theme.spacing(2),
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}));

interface ExpandableCardProps {
  id: string;
  title: string;
  children: ReactNode | ReactNode[];
  expandIcon?: ReactNode;
  className?: string;
  cardHeaderProps?: object;
  titleProps?: object;
  isExpanded: boolean;
  hideToggle?: boolean;
}

const ExpandableCard: React.FC<ExpandableCardProps> = ({
  id = '',
  className,
  title,
  titleProps,
  cardHeaderProps,
  children,
  expandIcon,
  isExpanded,
  hideToggle,
}) => {
  const { classes, cx } = useStyles();
  const [expanded, setExpanded] = useState<boolean>();

  useEffect(() => {
    setExpanded(hideToggle || isExpanded);
  }, [hideToggle, isExpanded]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
    // dispatch action to clear condition
  };

  const getExpandIcon = () => {
    if (hideToggle) return <></>;

    if (expandIcon) {
      return <Box onClick={handleExpandClick}>{expandIcon}</Box>;
    }

    return (
      <IconButton
        id='expandButton'
        className={expanded ? classes.expandOpen : classes.expand}
        onClick={handleExpandClick}
        aria-expanded={expanded}
        aria-label='show more'
      >
        <ExpandMoreIcon />
      </IconButton>
    );
  };

  return (
    <Card raised={false} elevation={0} className={cx(classes.root, className)} id={`${id}-expandable-card`}>
      <CardHeader
        sx={cardHeaderProps}
        title={title}
        titleTypographyProps={{ ...titleProps }}
        action={getExpandIcon()}
      />
      <Collapse in={expanded} timeout='auto' unmountOnExit>
        {expanded && <CardContent>{children}</CardContent>}
      </Collapse>
    </Card>
  );
};

export default ExpandableCard;
